/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  PaymentDetailsFields,
  RespLawyerFields,
  FirstPartnerFields,
  SecondPartnerFields,
  PaymentHoldReviewFields,
} from '../../../../types';
import { FormState } from '../../../container/forms/types/core/form-state.types';
import { PaymentWorkflowType } from '../../../container/forms/types/api/graph.types';
import { mapSection } from './utils';
import { mapSectionKey } from './workflowHelpers';
import {
  mapFieldsToFormState,
  handleClientMatterNumberToForm,
  applyTransferRequisitionPaymentDetailsToForm,
  mapGridData,
  mapDocuments,
} from './fieldMapping';
import { createLogger } from '../../../../utils/logger';
import {
  enrichOvertimeWorkedRow,
  extractOvertimeWorkedRows,
  normalizeLineManagerForForm,
  OVERTIME_WORKED_GRID_KEY,
  toOvertimeFormGridData,
} from '../../../container/forms/schemas/OvertimeApproval/utils';
import { mapGiftHospitalityDonationSectionToFormState } from './giftHospitalityDonationMapper';
// PLOP_INJECT_WORKFLOW_TO_FORM_IMPORTS

const logger = createLogger('WorkflowMapper');

export type FormDataSection = {
  sectionKey: string;
  fields: Record<string, any>;
  gridData?: any;
  documents?: any;
};

/**
 * Map a single workflow section into form state.
 * Dispatches by workflow type and mapped section key; merges fields/documents/grid into formState.data.
 */
export const mapWorkflowSectionToFormState = (
  section: FormDataSection,
  workflowType: PaymentWorkflowType,
  formState: FormState,
): void => {
  const mappedSectionKey = mapSectionKey(section.sectionKey, workflowType);

  switch (workflowType) {
    case PaymentWorkflowType.DubaiPaymentApproval:
    case PaymentWorkflowType.Undefined:
      mapDubaiPaymentApprovalSection(mappedSectionKey, section, formState);
      break;
    case PaymentWorkflowType.DubaiPaymentFromOfficeAcc:
      mapDubaiPaymentFromOfficeAccSection(mappedSectionKey, section, formState);
      break;
    case PaymentWorkflowType.DubaiTransferRequisition:
      mapDubaiTransferRequisitionSection(mappedSectionKey, section, formState);
      break;
    case PaymentWorkflowType.OvertimeApproval:
      mapOvertimeApprovalSection(mappedSectionKey, section, formState);
      break;
    case PaymentWorkflowType.GiftHospitalityDonationUk:
    case PaymentWorkflowType.GiftHospitalityDonationDubai:
    case PaymentWorkflowType.GiftHospitalityDonationDublin:
      mapGiftHospitalityDonationSectionToFormState(section, formState);
      break;
// PLOP_INJECT_WORKFLOW_TO_FORM_CASE
    default:
      break;
  }
};

const mapDubaiPaymentApprovalSection = (
  mappedSectionKey: string,
  section: FormDataSection,
  formState: FormState,
): void => {
  switch (mappedSectionKey) {
    case 'payment_details': {
      const paymentFields = mapSection<PaymentDetailsFields>(
        section.fields as PaymentDetailsFields,
      );
      applyPaymentDetailsClientMatterNames(paymentFields);
      applySecondPartnerMirror(paymentFields);
      const gridDataRows = buildPaymentDetailsGridRows(section, paymentFields);
      paymentFields.gridData = { data: gridDataRows };
      if (section.documents) Object.assign(formState.data, section.documents);
      Object.assign(formState.data, paymentFields);
      break;
    }
    case 'resp_lawyer': {
      const respLawyerFields = mapSection<RespLawyerFields>(
        section.fields as RespLawyerFields,
      );
      if (section.documents) Object.assign(respLawyerFields, section.documents);
      Object.assign(formState.data, respLawyerFields);
      break;
    }
    case 'first_partner':
      Object.assign(
        formState.data,
        mapSection<FirstPartnerFields>(section.fields as FirstPartnerFields),
      );
      break;
    case 'second_partner':
      Object.assign(
        formState.data,
        mapSection<SecondPartnerFields>(section.fields as SecondPartnerFields),
      );
      break;
    case 'payment_hold_review':
      Object.assign(
        formState.data,
        mapSection<PaymentHoldReviewFields>(
          section.fields as PaymentHoldReviewFields,
        ),
      );
      break;
    case 'payment_confirmation': {
      const fields = mapSection<any>(section.fields);
      if (section.documents) Object.assign(fields, section.documents);
      Object.assign(formState.data, fields);
      break;
    }
    case 'document_saving':
      Object.assign(formState.data, mapSection<any>(section.fields));
      break;
  }
};

const applyPaymentDetailsClientMatterNames = (
  paymentFields: Record<string, any>,
): void => {
  if (paymentFields.client) {
    paymentFields.client_name = paymentFields.client_code
      ? `${paymentFields.client_name} - (${paymentFields.client_code})`
      : paymentFields.client;
  }
  if (paymentFields.matter) {
    paymentFields.matter_name = paymentFields.matter_code
      ? `${paymentFields.matter_name} - (${paymentFields.matter_code})`
      : paymentFields.matter;
  }
};

const applySecondPartnerMirror = (paymentFields: Record<string, any>): void => {
  if (!paymentFields.second_partner) return;
  if (
    !paymentFields.second_partner_above_25k ||
    (Array.isArray(paymentFields.second_partner_above_25k) &&
      paymentFields.second_partner_above_25k.length === 0)
  ) {
    paymentFields.second_partner_above_25k = paymentFields.second_partner;
  }
  if (
    !paymentFields.second_partner_below_25k ||
    (Array.isArray(paymentFields.second_partner_below_25k) &&
      paymentFields.second_partner_below_25k.length === 0)
  ) {
    paymentFields.second_partner_below_25k = paymentFields.second_partner;
  }
};

const buildPaymentDetailsGridRows = (
  section: FormDataSection,
  paymentFields: Record<string, any>,
): any[] => {
  const gridData = section.gridData as any;
  if (
    gridData?.payment_details &&
    Array.isArray(gridData.payment_details) &&
    gridData.payment_details.length > 0
  ) {
    return gridData.payment_details.map((payment: any) => ({
      payee: payment.payee || '',
      currency: payment.currency || '',
      currencyCustom: payment.other_currency || '',
      amount: payment.amount || '',
      location: payment.location || '',
      accno: payment.account_number || '',
      iban: payment.iban || '',
      swiftBicCode: payment.swift_bic || '',
      bankName: payment.bank_name || '',
      bankAddress: payment.bank_address || '',
      purpose: payment.purpose || '',
      purposeCustom: payment.other_purpose || '',
      type: payment.type || '',
      deductBankCharges: payment.deduct_charges || '',
      reference: payment.reference || '',
      ledgerDesc: payment.ledger_desc || '',
    }));
  }
  const hasPaymentData =
    paymentFields.payee ||
    paymentFields.currency ||
    paymentFields.amount ||
    paymentFields.purpose;
  if (hasPaymentData) {
    return [
      {
        payee: paymentFields.payee || '',
        currency: paymentFields.currency || '',
        currencyCustom: paymentFields.other_currency || '',
        amount: paymentFields.amount || '',
        location: paymentFields.location || '',
        accno: paymentFields.account_number || '',
        iban: paymentFields.iban || '',
        swiftBicCode: paymentFields.swift_bic || '',
        bankName: paymentFields.bank_name || '',
        bankAddress: paymentFields.bank_address || '',
        purpose: paymentFields.purpose || '',
        purposeCustom: paymentFields.other_purpose || '',
        type: paymentFields.type || '',
        deductBankCharges: paymentFields.deduct_charges || '',
        reference: paymentFields.reference || '',
        ledgerDesc: paymentFields.ledger_desc || '',
      },
    ];
  }
  return [];
};

const mapDubaiPaymentFromOfficeAccSection = (
  mappedSectionKey: string,
  section: FormDataSection,
  formState: FormState,
): void => {
  logger.debug('Processing DubaiPaymentFromOfficeAcc section', {
    mappedSectionKey,
    sectionFields: section.fields,
    sectionGridData: section.gridData,
  });

  switch (mappedSectionKey) {
    case 'dubai_payment_from_office_account_payment_details': {
      const fields = section.fields || {};
      applyOfficeAccClientMatterNames(fields);
      handleClientMatterNumberToForm(fields, formState);
      mapFieldsToFormState(fields, formState, [
        'client_code',
        'matter_code',
        'client_matter_number',
      ]);
      mapDocuments(section.documents, formState);
      mapGridData(section.gridData, formState);
      break;
    }
    case 'dubai_payment_from_office_account_approver': {
      const fields = section.fields || {};
      const authApprove = fields.approver_auth_approve;
      const approvalDetails = fields.approver_approval_details;
      if (authApprove === 'yes') {
        formState.data.approverApprovalDetails = approvalDetails || '';
        formState.data.approverRejectionDetails = '';
      } else if (authApprove === 'no') {
        formState.data.approverRejectionDetails = approvalDetails || '';
        formState.data.approverApprovalDetails = '';
      }
      formState.data.approverAuthApprove = authApprove;
      mapFieldsToFormState(fields, formState, [
        'approver_auth_approve',
        'approver_approval_details',
      ]);
      break;
    }
    case 'dubai_payment_from_office_account_payment_confirmation': {
      const fields = section.fields || {};
      const authApprove = fields.payment_confirmation_auth_approve;
      const approvalDetails = fields.payment_confirmation_approval_details;
      if (authApprove === 'yes') {
        formState.data.paymentConfirmationApprovalDetails =
          approvalDetails || '';
        formState.data.paymentConfirmationRejectionDetails = '';
      } else if (authApprove === 'no') {
        formState.data.paymentConfirmationRejectionDetails =
          approvalDetails || '';
        formState.data.paymentConfirmationApprovalDetails = '';
      }
      formState.data.paymentConfirmationAuthApprove = authApprove;
      mapFieldsToFormState(fields, formState, [
        'payment_confirmation_auth_approve',
        'payment_confirmation_approval_details',
      ]);
      mapDocuments(section.documents, formState);
      break;
    }
  }
};

const applyOfficeAccClientMatterNames = (fields: Record<string, any>): void => {
  if (fields.client) {
    fields.client_name = fields.client_code
      ? `${fields.client_name || fields.client} - (${fields.client_code})`
      : fields.client_name || fields.client;
  }
  if (fields.matter) {
    fields.matter_name = fields.matter_code
      ? `${fields.matter_name || fields.matter} - (${fields.matter_code})`
      : fields.matter_name || fields.matter;
  }
};

const mapDubaiTransferRequisitionSection = (
  mappedSectionKey: string,
  section: FormDataSection,
  formState: FormState,
): void => {
  logger.debug('Processing DubaiTransferRequisition section', {
    mappedSectionKey,
    sectionFields: section.fields,
    sectionGridData: section.gridData,
  });

  switch (mappedSectionKey) {
    case 'dubai_transfer_requisition_payment_details': {
      const fields = section.fields || {};
      const debitClientCode = fields.debit_client_code;
      const debitMatterCode = fields.debit_matter_code;
      const debitClientMatterNumber = fields.debit_client_matter_number;
      if (debitClientCode && debitMatterCode) {
        formState.data.debitClientMatterNumber = `${debitClientCode} - ${debitMatterCode}`;
      } else if (debitClientCode) {
        formState.data.debitClientMatterNumber = debitClientCode;
      } else if (debitMatterCode) {
        formState.data.debitClientMatterNumber = debitMatterCode;
      } else if (debitClientMatterNumber) {
        formState.data.debitClientMatterNumber = debitClientMatterNumber;
      }
      const creditClientCode = fields.credit_client_code;
      const creditMatterCode = fields.credit_matter_code;
      const creditClientMatterNumber = fields.credit_client_matter_number;
      if (creditClientCode && creditMatterCode) {
        formState.data.creditClientMatterNumber = `${creditClientCode} - ${creditMatterCode}`;
      } else if (creditClientCode) {
        formState.data.creditClientMatterNumber = creditClientCode;
      } else if (creditMatterCode) {
        formState.data.creditClientMatterNumber = creditMatterCode;
      } else if (creditClientMatterNumber) {
        formState.data.creditClientMatterNumber = creditClientMatterNumber;
      }
      applyTransferRequisitionPaymentDetailsToForm(fields, formState);
      mapFieldsToFormState(fields, formState, [
        'debit_client_code',
        'debit_matter_code',
        'debit_client_matter_number',
        'credit_client_code',
        'credit_matter_code',
        'credit_client_matter_number',
      ]);
      mapDocuments(section.documents, formState);
      mapGridData(section.gridData, formState);
      break;
    }
    case 'dubai_transfer_requisition_first_partner': {
      const fields = section.fields || {};
      const authApprove = fields.first_partner_approval;
      const approvalDetails = fields.first_partner_auth_approve_comments;
      if (authApprove === 'yes') {
        formState.data.firstPartnerApproval = approvalDetails || '';
        formState.data.firstPartnerRejectionDetails = '';
      } else if (authApprove === 'no') {
        formState.data.firstPartnerRejectionDetails = approvalDetails || '';
        formState.data.firstPartnerApproval = '';
      }
      formState.data.firstPartnerApproval = authApprove;
      mapFieldsToFormState(fields, formState, ['first_partner_approval']);
      break;
    }
    case 'dubai_transfer_requisition_second_partner': {
      const fields = section.fields || {};
      const authApprove = fields.second_partner_approval;
      const approvalDetails = fields.second_partner_auth_approve_comments;
      if (authApprove === 'yes') {
        formState.data.secondPartnerApproval = approvalDetails || '';
        formState.data.secondPartnerRejectionDetails = '';
      } else if (authApprove === 'no') {
        formState.data.secondPartnerRejectionDetails = approvalDetails || '';
        formState.data.secondPartnerApproval = '';
      }
      formState.data.secondPartnerApproval = authApprove;
      mapFieldsToFormState(fields, formState, ['second_partner_approval']);
      break;
    }
    case 'dubai_transfer_requisition_payment_confirmation': {
      // Keep snake_case API field names (matches form schema + submit mapper)
      const fields = mapSection(section.fields || {});
      if (section.documents) Object.assign(fields, section.documents);
      Object.assign(formState.data, fields);
      break;
    }
  }
};

const mapOvertimeApprovalSection = (
  mappedSectionKey: string,
  section: FormDataSection,
  formState: FormState,
): void => {
  logger.debug('Processing OvertimeApproval section', {
    mappedSectionKey,
    sectionFields: section.fields,
    sectionGridData: section.gridData,
  });

  switch (mappedSectionKey) {
    case 'overtime_approval_request_details': {
      const fields = mapSection(section.fields || {});
      fields.line_manager = normalizeLineManagerForForm(fields.line_manager);
      Object.assign(formState.data, fields);
      mapDocuments(section.documents, formState);
      formState.data[OVERTIME_WORKED_GRID_KEY] = toOvertimeFormGridData(
        extractOvertimeWorkedRows(section.gridData).map(enrichOvertimeWorkedRow)
      );
      break;
    }
    case 'overtime_approval_line_manager':
    case 'overtime_approval_finance_confirmation': {
      const fields = mapSection(section.fields || {});
      Object.assign(formState.data, fields);
      break;
    }
  }
};
